// Astra Today's client state (#3754, ADR 0041): ONE copy of the student's
// moves for every surface that shows them (the /today page, Astra's sidebar
// row and the phone pill, the opener on /astra), so the badge and the page
// can never disagree. The server decides everything; this only holds what it
// said last and asks again when a surface needs it.
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import { getAstraMoves, respondAstraMove } from './api';

const REFRESH_MS = 5 * 60 * 1000;
export const PILOT_KEY = 'astra_today_pilot';
const EMPTY = { email: null, loaded: false, failed: false, moves: [], listCounts: null,
    generatedAt: null, quietLine: null, branding: {}, firstName: null, question: null };

let state = { ...EMPTY };
const listeners = new Set();
let inflight = null;
let lastFetch = 0;

function set(next) {
    state = { ...state, ...next };
    listeners.forEach((fn) => fn(state));
}

/** Open moves only: a Later one is the student's own choice to wait. */
export const openMoves = (moves) => (moves || []).filter((m) => m && m.state !== 'later');
/** The move a "1 thing for you" door opens on (#3786): the top open one, as /today shows it first. */
export const topMoveId = (today) => (today?.enabled ? openMoves(today.moves)[0]?.id || null : null);

/** The B2 line: "1 thing for you", "2 things for you". */
export const thingsLine = (n) => `${n} ${n === 1 ? 'thing' : 'things'} for you`;

let retried = false;

export function loadAstraToday(email, { recompute = false, force = false } = {}) {
    if (!email) return Promise.resolve(state);
    if (state.email !== email) state = { ...EMPTY, email };
    if (inflight && !recompute) return inflight;
    if (!force && !recompute && state.loaded && Date.now() - lastFetch < REFRESH_MS) return Promise.resolve(state);
    inflight = getAstraMoves(email, { recompute }).then((res) => {
        lastFetch = Date.now();
        if (res && res.success !== false) {
            set({ email, loaded: true, failed: false, moves: res.moves || [],
                listCounts: res.list_counts || null, generatedAt: res.generated_at || null,
                quietLine: res.quiet_line || null, branding: res.branding || {},
                firstName: res.first_name || null, question: res.question || null });
        } else {
            // A failed read is unknown, never "nothing needs you" (app-page
            // honesty rule): keep what we had and say it failed.
            set({ email, loaded: true, failed: true });
            // One quiet retry: a cold first page load left the badge blank
            // for the whole 5-minute window (UX walk 2026-10-11, #3810).
            if (!res?.off && !retried) {
                retried = true;
                setTimeout(() => { loadAstraToday(email, { force: true }); }, 3000);
            }
        }
        return state;
    }).finally(() => { inflight = null; });
    return inflight;
}

/** Send one answer; the server returns the moves after it. `apply` false
 * holds the page as it is (a card still asking "why?" must not vanish under
 * the student's thumb); the caller applies the result with applyMoves. */
export async function answerMove(email, moveId, response, option = null, { apply = true } = {}) {
    const res = await respondAstraMove(email, moveId, response, option);
    if (apply && res?.success && Array.isArray(res.moves)) set({ moves: res.moves });
    return res;
}

/** Put the server's latest moves on every surface (the badge too). */
export function applyMoves(moves) {
    if (Array.isArray(moves)) set({ moves });
}

/**
 * The student's moves for a surface. `recompute` asks the server to run the
 * rules now (the /today page); everyone else reads what is stored.
 * Returns { enabled, ...state }; enabled is false off the pilot flag.
 */
export function useAstraToday({ recompute = false } = {}) {
    const { currentUser } = useAuth();
    const email = currentUser?.email || null;
    const enabled = useFeatureFlag('astra_today');
    const [snap, setSnap] = useState(state);

    useEffect(() => {
        listeners.add(setSnap);
        return () => listeners.delete(setSnap);
    }, []);

    useEffect(() => {
        if (!email || !enabled) return undefined;
        // InstallPrompt sits outside the flags provider: this marker tells it
        // the iPhone Home Screen ask belongs to Astra Today's moment (C1).
        try { sessionStorage.setItem(PILOT_KEY, '1'); } catch { /* private mode */ }
        loadAstraToday(email, { recompute });
        const onFocus = () => loadAstraToday(email);
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [email, enabled, recompute]);

    const mine = snap.email === email ? snap : EMPTY;
    return { enabled: Boolean(email && enabled), ...mine };
}

// Tests reset the module's single copy between cases.
export function __resetAstraToday() {
    state = { ...EMPTY };
    inflight = null;
    lastFetch = 0;
    retried = false;
}

/** The question card is done with (answered, skipped, ended): off every surface. */
export function clearQuestion() {
    set({ question: null });
}
